import { Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { VyIcon } from '@weft/icons';
import { DefaultThemeDialog } from 'apps/app/src/components/design/DefaultThemeDialog';
import { useDeleteContract } from 'apps/app/src/hooks/requests/use-delete-contract';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import React from 'react';
import { useQueryClient } from 'react-query';
import { WarningIcon } from '../../../../../components/icons/WarningIcon';
import { useDialog } from '../../../../../hooks/use-dialog';
import { useVyNavigate } from '../../../../../hooks/use-vy-navigate';

type DeleteContractMenuItemProps = {
  contractId: string;
  disabled?: boolean;
  contractName?: string;
};

export const DeleteContractMenuItem: React.FC<DeleteContractMenuItemProps> = ({ contractId, disabled, contractName }) => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const navigate = useVyNavigate();
  const deleteContractMutation = useDeleteContract({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: query => ['contracts', contractId].some(key => query.queryKey.includes(key)),
      });

      navigate('/plans?tab=contracts');
    },
  });
  const queryClient = useQueryClient();

  const handleDialogSubmit = async () => {
    handleClose();
    await runMutation(
      deleteContractMutation,
      contractId, {
        pending: 'Deleting...',
        success: 'Deleted',
        error: 'Failed to delete contract',
      },
    );
  };
  
  return (
    <>
      <div>
        <MenuItem
          disabled={disabled}
          onClick={handleOpen}
          sx={{ padding: '5px 8px' }}
        >
          <IconButton
            sx={{
  
              position: 'sticky',
              top: '10px',
              '&:hover': {
                background: 'none',
              },
            }}
          >
            <VyIcon
              color="#D32F2F"
              icon="Delete"
            />
          </IconButton>
          <Typography color="#D32F2F">Delete</Typography>
        </MenuItem>
  
        <DefaultThemeDialog
          open={isDialogOpen}
          onClose={handleClose}
          fullWidth
          PaperProps={{
            className: 'max-w-[780px]',
          }}
        >
            
          <DialogTitle>
            {`Are you sure you want to delete ${contractName}`}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>This function will permanently delete the contract and all the related invoices.</DialogContentText>
          </DialogContent>
  
          <Divider className="my-6"/>
  
          <DialogActions>
            <Button
              variant="text"
              color="secondary"
              onClick={handleClose}
              disabled={deleteContractMutation.isLoading}
            >
              Cancel
            </Button>
  
            <button
              type="button"
              onClick={handleDialogSubmit}
              disabled={deleteContractMutation.isLoading}
              className="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-base font-medium
                 text-blue-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 enabled:hover:bg-blue-200
                 disabled:opacity-50"
            >
              Delete contract
            </button>
          </DialogActions>
  
          <Divider className="my-6"/>
  
          <div className="flex items-center gap-x-3">
            <WarningIcon width={40}/>
            <Typography>Once done, you will not be able to recover or resume these items</Typography>
          </div>
        </DefaultThemeDialog>
      </div>
    </>
  );
};